// WishBlock CRUD — the typed client the layout editor actually uses.
//
// W49.0 C2. `WishEditLayout` used to hand-roll all five of these with bare
// `fetch`, and that cost it twice over:
//
//   1. Every failure threw a plain `Error`, so `messageFor()` in
//      `mutationErrorReporter.tsx` (which only recognises `ApiError`) fell
//      through to the generic "network failed" copy. The backend had been
//      answering 「同類型多個區塊時必須填寫標題」 the whole time and the screen
//      never showed it — a predictable 422 presented as a dead network.
//   2. Two of the five sent a **body the contract does not accept**, which was
//      not caught by the first problem because the error was being swallowed
//      into generic copy. See the two notes on the functions below.
//
// Split out of `wishlist.ts` rather than added to it: that file is pinned in
// `lib-size-baseline.json` at its ceiling (20,480 bytes / 500 lines of code)
// and the ratchet fails a baseline file that grows. `call` is exported from
// there so there is still exactly one funnel — a second copy is how the two
// paths diverge in the first place.
//
// Schema authority: `backend/app/modules/wishlist/schemas.py`.

import { call, type WishBlockOut } from "./wishlist";
import type { BlockReorderItem as SchemaBlockReorderItem } from "./schema";
import type {
  ChecklistItemCreate as SchemaChecklistItemCreate,
  ChecklistItemOut as SchemaChecklistItemOut,
  LinkItemCreate as SchemaLinkItemCreate,
  LinkItemOut as SchemaLinkItemOut,
  PhotoAttachRequest as SchemaPhotoAttachRequest,
  ProgressLogCreate as SchemaProgressLogCreate,
  ProgressLogOut as SchemaProgressLogOut,
  ProgressLogUpdate as SchemaProgressLogUpdate,
} from "./schema";

// --- Request payloads ----------------------------------------------------

export interface WishBlockCreate {
  block_type: WishBlockOut["block_type"];
  title: string;
  data?: Record<string, unknown>;
  position?: number | null;
}

export interface WishBlockUpdate {
  title?: string;
  data?: Record<string, unknown>;
}

// --- Client -------------------------------------------------------------

export async function createWishBlock(
  wishId: string,
  payload: WishBlockCreate,
): Promise<WishBlockOut> {
  return call<WishBlockOut>("POST", `/api/v1/wishes/${wishId}/blocks`, { body: payload });
}

export async function patchWishBlock(
  wishId: string,
  blockId: string,
  payload: WishBlockUpdate,
): Promise<WishBlockOut> {
  return call<WishBlockOut>("PATCH", `/api/v1/wishes/${wishId}/blocks/${blockId}`, {
    body: payload,
  });
}

/** `DELETE .../blocks/{bid}` answers `204` — see `expectBody` in `wishlist.ts`. */
export async function deleteWishBlock(wishId: string, blockId: string): Promise<void> {
  await call<null>("DELETE", `/api/v1/wishes/${wishId}/blocks/${blockId}`, {
    expectBody: false,
  });
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type BlockReorderItem = SchemaBlockReorderItem;

export async function reorderBlocks(
  wishId: string,
  items: BlockReorderItem[],
): Promise<WishBlockOut[]> {
  // POST, not PATCH (ADR-0097). The backend routes this path as POST; PATCH
  // matched `/blocks/{block_id}` instead, parsed the literal string "reorder"
  // as a block id, and answered **422 「欄位驗證失敗 path.block_id」**.
  //
  // The body is `{ items }` — `BlockReorderRequest`. The layout editor used to
  // post a bare array from its own `fetch`, which Pydantic rejects for the same
  // reason, so every reorder failed while the local order state made it look
  // like it had worked.
  return call<WishBlockOut[]>("POST", `/api/v1/wishes/${wishId}/blocks/reorder`, {
    body: { items },
  });
}

/**
 * Default `data` for a newly added block, per block type.
 *
 * Moved here from the page so the shape a block is created with has one home.
 * `progress` and `budget` matter to the backend: `recompute_wish_progress`
 * reads `mode` and `target_amount` out of `data`, and a `budget` block with no
 * `amount` has nothing to compute a percentage from.
 */
export function defaultBlockData(blockType: WishBlockOut["block_type"]): Record<string, unknown> {
  switch (blockType) {
    case "progress":
      return { mode: "percent" };
    case "budget":
      return { amount: 0, currency: "TWD" };
    case "notes":
      return { body: "" };
    default:
      return {};
  }
}

// --- Block sub-resources (progress log · checklist · links · photos) -------
//
// W50.3 D-1. Moved here from `wishlist.ts`, which was 326 bytes from its
// recorded ceiling and could not take the one function this sprint added.
// The seam is the key: everything below is addressed by **wishId + blockId**,
// the same key this file already used for block CRUD. The sections that stay
// in `wishlist.ts` are addressed by **wishId alone**.
//
// This file had already absorbed `reorderBlocks` for the same reason; see the
// header at the top. §3 #13 — reuse before create.
// --- Sub-table CRUD: progress log (per API_REFERENCE §5.3) ------------

/** Progress / budget log entry returned by the backend. */
export type ProgressLogOut = SchemaProgressLogOut;

/** Embedded block summary returned with the log list (per DATA_MODEL §5.5). */
export interface ProgressBlockSummary {
  percent: number;
  spent: number;
  target: number | null;
  /** "expense" / "income" / "both" — controls the Direction toggle UI. */
  direction: "expense" | "income" | "both";
}

export interface ProgressLogListResponse {
  items: ProgressLogOut[];
  total: number;
  block_summary: ProgressBlockSummary;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type ProgressLogCreate = SchemaProgressLogCreate;

export async function listProgressLog(
  wishId: string,
  blockId: string,
  args: { limit?: number; offset?: number } = {},
): Promise<ProgressLogListResponse> {
  return call<ProgressLogListResponse>("GET", `/api/v1/wishes/${wishId}/blocks/${blockId}/log`, {
    params: {
      query: {
        limit: args.limit ?? 50,
        offset: args.offset ?? 0,
      } as Record<string, unknown>,
    },
  });
}

export async function addProgressLog(
  wishId: string,
  blockId: string,
  payload: ProgressLogCreate,
): Promise<ProgressLogOut> {
  return call<ProgressLogOut>("POST", `/api/v1/wishes/${wishId}/blocks/${blockId}/log`, {
    body: payload,
  });
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type ProgressLogUpdate = SchemaProgressLogUpdate;

/**
 * W50.3 D-1 — the missing U of the four.
 *
 * Both fields optional, and **omitted means "leave it alone"**. The backend
 * reads `exclude_unset` precisely so this can be true: `note` is nullable, and
 * an explicit `null` there means "clear it", which a defaulted-`None` payload
 * could not express. Build the body by assigning only what changed —
 * `{ ...(amount !== undefined && { amount_delta: amount }) }` — because sending
 * `note: undefined` and omitting the key are the same on the wire, but sending
 * `note: null` is not.
 */
export async function updateProgressLog(
  wishId: string,
  blockId: string,
  logId: string,
  payload: ProgressLogUpdate,
): Promise<ProgressLogOut> {
  return call<ProgressLogOut>(
    "PATCH",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/log/${logId}`,
    { body: payload },
  );
}

export async function deleteProgressLog(
  wishId: string,
  blockId: string,
  logId: string,
): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/blocks/${blockId}/log/${logId}`, {
    expectBody: false,
  });
}

// --- Sub-table CRUD: checklist (per API_REFERENCE §5.1) --------------

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type ChecklistItemOut = SchemaChecklistItemOut;

export interface ChecklistItemListResponse {
  items: ChecklistItemOut[];
  total: number;
  progress: { checked: number; total: number };
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type ChecklistItemCreate = SchemaChecklistItemCreate;

export interface ChecklistItemUpdate {
  content?: string;
  is_checked?: boolean;
  position?: number | null;
}

export async function listChecklistItems(
  wishId: string,
  blockId: string,
): Promise<ChecklistItemListResponse> {
  return call<ChecklistItemListResponse>(
    "GET",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/checklist-items`,
  );
}

export async function addChecklistItem(
  wishId: string,
  blockId: string,
  payload: ChecklistItemCreate,
): Promise<ChecklistItemOut> {
  return call<ChecklistItemOut>(
    "POST",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/checklist-items`,
    { body: payload },
  );
}

export async function patchChecklistItem(
  wishId: string,
  blockId: string,
  itemId: string,
  payload: ChecklistItemUpdate,
): Promise<ChecklistItemOut> {
  return call<ChecklistItemOut>(
    "PATCH",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/checklist-items/${itemId}`,
    { body: payload },
  );
}

export async function deleteChecklistItem(
  wishId: string,
  blockId: string,
  itemId: string,
): Promise<void> {
  return call<void>(
    "DELETE",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/checklist-items/${itemId}`,
    { expectBody: false },
  );
}

// --- Sub-table CRUD: links (per API_REFERENCE §5.2) ------------------

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type LinkItemOut = SchemaLinkItemOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type LinkItemCreate = SchemaLinkItemCreate;

export interface LinkItemUpdate {
  title?: string;
  description?: string | null;
  position?: number | null;
}

export async function listLinkItems(wishId: string, blockId: string): Promise<LinkItemOut[]> {
  return call<LinkItemOut[]>("GET", `/api/v1/wishes/${wishId}/blocks/${blockId}/links`);
}

export async function addLinkItem(
  wishId: string,
  blockId: string,
  payload: LinkItemCreate,
): Promise<LinkItemOut> {
  return call<LinkItemOut>("POST", `/api/v1/wishes/${wishId}/blocks/${blockId}/links`, {
    body: payload,
  });
}

export async function patchLinkItem(
  wishId: string,
  blockId: string,
  itemId: string,
  payload: LinkItemUpdate,
): Promise<LinkItemOut> {
  return call<LinkItemOut>("PATCH", `/api/v1/wishes/${wishId}/blocks/${blockId}/links/${itemId}`, {
    body: payload,
  });
}

export async function deleteLinkItem(
  wishId: string,
  blockId: string,
  itemId: string,
): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/blocks/${blockId}/links/${itemId}`, {
    expectBody: false,
  });
}

// --- Sub-table CRUD: photos (per API_REFERENCE §5.4) -----------------

export interface PhotoItemOut {
  album_item_id: string;
  position: number;
  url: string | null;
  caption: string | null;
  taken_at: string | null;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type PhotoAttachRequest = SchemaPhotoAttachRequest;

export async function listPhotoItems(wishId: string, blockId: string): Promise<PhotoItemOut[]> {
  return call<PhotoItemOut[]>("GET", `/api/v1/wishes/${wishId}/blocks/${blockId}/photos`);
}

export async function attachPhotos(
  wishId: string,
  blockId: string,
  albumItemIds: string[],
): Promise<PhotoItemOut[]> {
  return call<PhotoItemOut[]>("POST", `/api/v1/wishes/${wishId}/blocks/${blockId}/photos`, {
    body: { album_item_ids: albumItemIds } satisfies PhotoAttachRequest,
  });
}

export async function detachPhoto(
  wishId: string,
  blockId: string,
  albumItemId: string,
): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/blocks/${blockId}/photos/${albumItemId}`, {
    expectBody: false,
  });
}

export async function reorderPhotos(
  wishId: string,
  blockId: string,
  positions: { album_item_id: string; position: number }[],
): Promise<PhotoItemOut[]> {
  return call<PhotoItemOut[]>(
    "PATCH",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/photos/reorder`,
    { body: { positions } },
  );
}
